import React from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  SimpleGrid,
  Spinner,
  Icon,
  Badge,
  List,
  ListItem,
  Heading,
  HStack,
  VStack,
  Grid,
  GridItem,
  Divider,
  Progress,
} from "@chakra-ui/react";
import { 
  FaChartLine, 
  FaShoppingCart, 
  FaBoxOpen, 
  FaStore, 
  FaInfoCircle, 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaBox
} from "react-icons/fa";
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
// اتصال واقعی به سرویس‌های بک‌اند شما
import { getOrdersStats } from '../services/orderService';
import { getProducts } from '../services/productService';
import { getGateways } from '../services/gatewayService';

// ==========================================
// تنظیمات گرافیکی کارت‌های آمار بالای صفحه
// ==========================================
const statCardsConfig = [
  {
    key: 'sales',
    label: 'تعداد کل سفارش‌ها',
    help: 'سفارش‌های ثبت‌شده',
    icon: FaChartLine,
    colorScheme: 'blue',
    bgLight: 'blue.50',
    bgDark: 'blue.900',
    iconColorLight: 'blue.500',
    iconColorDark: 'blue.300',
  },
  {
    key: 'quantity',
    label: 'واحدهای فروخته‌شده',
    help: 'مجموع آیتم‌ها',
    icon: FaShoppingCart,
    colorScheme: 'teal',
    bgLight: 'teal.50',
    bgDark: 'teal.900',
    iconColorLight: 'teal.500',
    iconColorDark: 'teal.300',
  },
  {
    key: 'products',
    label: 'تنوع محصولات',
    help: 'محصولات موجود در انبار',
    icon: FaBoxOpen,
    colorScheme: 'purple',
    bgLight: 'purple.50',
    bgDark: 'purple.900',
    iconColorLight: 'purple.500',
    iconColorDark: 'purple.300',
  },
  {
    key: 'gateways',
    label: 'درگاه‌های متصل',
    help: 'فروشگاه‌های فعال',
    icon: FaStore,
    colorScheme: 'orange',
    bgLight: 'orange.50',
    bgDark: 'orange.900',
    iconColorLight: 'orange.500',
    iconColorDark: 'orange.300',
  },
];

// ==========================================
// کامپوننت کارت آماری (Stat Card)
// ==========================================
function StatCard({ config, value, cardBg, borderColor, cardShadow, textColor, mutedColor }) {
  const iconBg = useColorModeValue(config.bgLight, 'whiteAlpha.100');
  const iconColor = useColorModeValue(config.iconColorLight, config.iconColorDark);
  
  return (
    <Box
      p={6}
      bg={cardBg}
      borderRadius="2xl"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow={cardShadow}
      position="relative"
      overflow="hidden"
      role="group"
      transition="all 0.3s cubic-bezier(.25,.8,.25,1)"
      _hover={{
        transform: "translateY(-4px)",
        boxShadow: "xl",
        borderColor: iconColor,
      }}
    >
      {/* افکت نوری پس‌زمینه (Glow) در هاور */}
      <Box 
        position="absolute" top="-20px" right="-20px" w="100px" h="100px" 
        bg={iconBg} filter="blur(40px)" opacity={0} 
        _groupHover={{ opacity: 0.6 }} transition="all 0.4s"
      />

      <Flex align="flex-start" justify="space-between" position="relative" zIndex={1}>
        <VStack align="start" spacing={1}>
          <Text fontSize="sm" color={mutedColor} fontWeight="600">
            {config.label}
          </Text>
          <Text fontSize="3xl" fontWeight="black" color={textColor} letterSpacing="tight">
            {typeof value === 'number' ? value.toLocaleString('fa-IR') : value}
          </Text>
          <HStack spacing={1} mt={1}>
            <Badge colorScheme={config.colorScheme} variant="subtle" fontSize="2xs" px={2} rounded="full">
              {config.help}
            </Badge>
          </HStack>
        </VStack>
        
        <Flex
          align="center" justify="center"
          w="56px" h="56px"
          borderRadius="xl"
          bg={iconBg} color={iconColor}
          boxShadow={`0 4px 14px 0 rgba(0,0,0,0.05)`}
          transform="rotate(0deg)"
          _groupHover={{ transform: "rotate(-10deg) scale(1.1)" }}
          transition="all 0.3s ease"
        >
          <Icon as={config.icon} boxSize={7} />
        </Flex>
      </Flex>
    </Box>
  );
}

// ==========================================
// کامپوننت اصلی داشبورد
// ==========================================
function Dashboard() {
  const { user } = useAuth();
  
  // Color Mode Variables برای پشتیبانی از Dark/Light Theme
  const bgPage = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.200");
  const cardShadow = useColorModeValue("0 4px 20px -2px rgba(0,0,0,0.05)", "0 4px 20px -2px rgba(0,0,0,0.4)");
  const gatewayHoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const progressBg = useColorModeValue('gray.100', 'whiteAlpha.100');

  const displayName = user?.firstName || user?.lastName
    ? [user.firstName, user.lastName].filter(Boolean).join(' ')
    : user?.phone || 'مدیر سیستم';

  // Fetching Data (دریافت واقعی از API)
  const { data: statsData, isLoading: statsLoading } = useQuery(
    ['orders-stats'],
    () => getOrdersStats(),
    { staleTime: 5 * 60 * 1000 }
  );

  const { data: productsData } = useQuery(
    ['products-count'],
    () => getProducts({ limit: 1 }),
    { staleTime: 5 * 60 * 1000 }
  );

  const { data: gatewaysData } = useQuery(
    ['gateways-count'],
    getGateways,
    { staleTime: 5 * 60 * 1000 }
  );

  const stats = statsData?.data || {};
  const totalProducts = productsData?.pagination?.total || productsData?.data?.length || 0;
  const totalGateways = gatewaysData?.data?.length || 0;

  const statValues = {
    sales: stats.totalSales || 0,
    quantity: stats.totalQuantity || 0,
    products: totalProducts,
    gateways: totalGateways,
  };

  // Loading State
  if (statsLoading) {
    return (
      <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} align="center" justify="center">
        <VStack spacing={6}>
          <Box position="relative">
            <Spinner size="xl" thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" w="60px" h="60px" />
            <Icon as={FaBox} position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" color="blue.500" />
          </Box>
          <Text fontSize="lg" fontWeight="bold" color={textColor} animation="pulse">
            در حال بارگذاری مرکز فرماندهی...
          </Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} pb={10}>
      
      {/* ========================================== */}
      {/* 1. بنر خوش‌آمدگویی (Hero Banner) */}
      {/* ========================================== */}
      <Box 
        w="full" 
        bgGradient="linear(to-l, blue.600, purple.600)" 
        pt={{ base: 8, md: 12 }} pb={{ base: 20, md: 24 }} 
        px={{ base: 6, md: 10 }}
        position="relative"
        overflow="hidden"
      >
        <Box position="absolute" top="-20%" left="-5%" w="300px" h="300px" bg="whiteAlpha.200" filter="blur(60px)" rounded="full" />
        <Box position="absolute" bottom="-20%" right="10%" w="250px" h="250px" bg="blackAlpha.200" filter="blur(60px)" rounded="full" />
        
        <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "start", md: "center" }} position="relative" zIndex={2} maxW="7xl" mx="auto">
          <VStack align="start" spacing={2} color="white">
            <Badge bg="whiteAlpha.200" color="white" px={3} py={1} rounded="full" fontSize="xs" backdropFilter="blur(10px)">
              داشبورد CoreSell
            </Badge>
            <Heading size="lg" fontWeight="black" letterSpacing="tight">
              سلام {displayName}، وقت بخیر! 👋
            </Heading>
            <Text fontSize="md" opacity={0.9}>
              گزارش لحظه‌ای از انبار و فروشگاه‌های شما آماده است.
            </Text>
          </VStack>
        </Flex>
      </Box>

      {/* ========================================== */}
      {/* 2. محتوای اصلی داشبورد */}
      {/* ========================================== */}
      <Box maxW="7xl" mx="auto" w="full" px={{ base: 4, md: 10 }} mt={{ base: -12, md: -14 }} position="relative" zIndex={3}>
        
        {/* کارت‌های آماری چهارگانه (بالا) */}
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6} mb={10}>
          {statCardsConfig.map((config) => (
            <StatCard
              key={config.key}
              config={config}
              value={statValues[config.key]}
              cardBg={cardBg}
              borderColor={borderColor}
              textColor={textColor}
              mutedColor={mutedColor}
              cardShadow={cardShadow}
            />
          ))}
        </SimpleGrid>

        <Grid templateColumns={{ base: "1fr", lg: "2fr 1.2fr" }} gap={8}>
          
          {/* ========================================== */}
          {/* ستون راست: لیست آمار فروش درگاه‌ها */}
          {/* ========================================== */}
          <GridItem>
            <Box bg={cardBg} p={6} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} boxShadow={cardShadow} h="full">
              <Flex justify="space-between" align="center" mb={6}>
                <HStack spacing={3}>
                  <Flex w="40px" h="40px" rounded="xl" bg="blue.50" color="blue.500" align="center" justify="center">
                    <Icon as={FaStore} boxSize={5} />
                  </Flex>
                  <Box>
                    <Heading size="md" color={textColor} letterSpacing="tight">عملکرد درگاه‌ها</Heading>
                    <Text fontSize="sm" color={mutedColor}>آمار فروش به تفکیک فروشگاه</Text>
                  </Box>
                </HStack>
              </Flex>

              <VStack spacing={4} align="stretch">
                {stats.salesByGateway && stats.salesByGateway.length > 0 ? (
                  stats.salesByGateway.map((item, index) => {
                    // تعیین رنگ بر اساس ایندکس (برای تنوع گرافیکی)
                    const gatewayColors = ['blue', 'red', 'orange', 'purple', 'teal'];
                    const colorScheme = gatewayColors[index % gatewayColors.length];
                    
                    // محاسبه درصد برای نوار پیشرفت (نسبت به بیشترین فروش)
                    const maxQty = Math.max(...stats.salesByGateway.map(g => g.totalQuantity || 0)) || 1;
                    const progressValue = ((item.totalQuantity || 0) / maxQty) * 100;

                    return (
                      <Box 
                        key={index} 
                        p={4} 
                        borderWidth="1px" 
                        borderColor={borderColor} 
                        borderRadius="xl"
                        _hover={{ bg: gatewayHoverBg }}
                        transition="all 0.2s"
                      >
                        <Flex justify="space-between" align="center" mb={3}>
                          <HStack>
                            <Box w={2} h={10} rounded="full" bg={`${colorScheme}.400`} />
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="bold" color={textColor}>{item.gatewayName}</Text>
                              <Text fontSize="xs" color={mutedColor}>{item.gatewayType || 'فروشگاه'}</Text>
                            </VStack>
                          </HStack>
                          <VStack align="end" spacing={0}>
                            <Text fontWeight="black" fontSize="lg" color={textColor}>
                              {item.totalQuantity?.toLocaleString('fa-IR')} <Text as="span" fontSize="xs" fontWeight="normal" color={mutedColor}>واحد</Text>
                            </Text>
                            <Text fontSize="xs" color={`${colorScheme}.500`} fontWeight="bold">
                              {item.count?.toLocaleString('fa-IR')} سفارش
                            </Text>
                          </VStack>
                        </Flex>
                        {/* نوار پیشرفت (Progress Bar) */}
                        <Progress value={progressValue} size="xs" colorScheme={colorScheme} rounded="full" bg={progressBg} />
                      </Box>
                    );
                  })
                ) : (
                  <Flex direction="column" align="center" justify="center" py={10} textAlign="center" bg={gatewayHoverBg} rounded="xl" border="1px dashed" borderColor={borderColor}>
                    <Icon as={FaBox} boxSize={10} color="gray.300" mb={3} />
                    <Text fontWeight="bold" color={textColor}>هنوز سفارشی ثبت نشده است</Text>
                    <Text fontSize="sm" color={mutedColor}>با ثبت اولین سفارش، آمار در اینجا نمایش داده می‌شود.</Text>
                  </Flex>
                )}
              </VStack>
            </Box>
          </GridItem>

          {/* ========================================== */}
          {/* ستون چپ: سلامت سیستم و راهنما */}
          {/* ========================================== */}
          <GridItem>
            <VStack spacing={6} h="full">
              
              {/* Alert مهم (تزئینی) */}
              <Box bgGradient="linear(to-br, blue.500, cyan.500)" p={6} borderRadius="2xl" color="white" w="full" position="relative" overflow="hidden" boxShadow="0 10px 25px -5px rgba(59,130,246,0.5)">
                <Icon as={FaInfoCircle} position="absolute" top="-20px" left="-20px" boxSize="120px" opacity={0.1} />
                <HStack align="start" spacing={4} position="relative" zIndex={1}>
                  <Box mt={1}>
                    <Icon as={FaInfoCircle} boxSize={6} />
                  </Box>
                  <Box>
                    <Text fontWeight="bold" fontSize="lg" mb={1}>هسته مرکزی انبار</Text>
                    <Text fontSize="sm" opacity={0.9} lineHeight="tall">
                      کدهای حسابداری کالاها (SKU) در سیستم، منبع اصلی همگام‌سازی هستند. تغییر موجودی در اینجا، به‌طور خودکار به تمام درگاه‌ها ارسال می‌شود.
                    </Text>
                  </Box>
                </HStack>
              </Box>

              {/* پنل وضعیت و راهنما */}
              <Box bg={cardBg} p={6} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} w="full" flex={1}>
                <Heading size="md" color={textColor} mb={6} letterSpacing="tight">
                  راهنمای همگام‌سازی (Sync)
                </Heading>

                <List spacing={5}>
                  <ListItem>
                    <HStack align="start" spacing={3}>
                      <Flex w="32px" h="32px" rounded="full" bg="green.50" color="green.500" align="center" justify="center" shrink={0}>
                        <Icon as={FaCheckCircle} />
                      </Flex>
                      <Box>
                        <Text fontWeight="bold" color={textColor} fontSize="sm">کسر خودکار پس از فروش</Text>
                        <Text fontSize="xs" color={mutedColor} mt={1}>موجودی بلافاصله پس از ثبت سفارش در هر کانالی، در تمام درگاه‌ها کسر می‌شود.</Text>
                      </Box>
                    </HStack>
                  </ListItem>
                  
                  <Divider borderColor={borderColor} />

                  <ListItem>
                    <HStack align="start" spacing={3}>
                      <Flex w="32px" h="32px" rounded="full" bg="purple.50" color="purple.500" align="center" justify="center" shrink={0}>
                        <Icon as={FaCheckCircle} />
                      </Flex>
                      <Box>
                        <Text fontWeight="bold" color={textColor} fontSize="sm">ادغام هوشمند تنوع‌ها</Text>
                        <Text fontSize="xs" color={mutedColor} mt={1}>سیستم کالاهایی با کد حسابداری یکسان را در قالب یک محصول با تنوع‌های منطقی ادغام می‌کند.</Text>
                      </Box>
                    </HStack>
                  </ListItem>

                  <Divider borderColor={borderColor} />

                  <ListItem>
                    <HStack align="start" spacing={3}>
                      <Flex w="32px" h="32px" rounded="full" bg="orange.50" color="orange.500" align="center" justify="center" shrink={0}>
                        <Icon as={FaExclamationTriangle} />
                      </Flex>
                      <Box>
                        <Text fontWeight="bold" color={textColor} fontSize="sm">اهمیت کدهای یکتا</Text>
                        <Text fontSize="xs" color={mutedColor} mt={1}>وارد کردن کدهای تکراری برای محصولات متفاوت باعث تداخل در همگام‌سازی می‌شود.</Text>
                      </Box>
                    </HStack>
                  </ListItem>
                </List>
              </Box>

            </VStack>
          </GridItem>
          
        </Grid>
      </Box>
    </Flex>
  );
}

export default Dashboard;